import React from 'react';
import * as yup from 'yup';
import {useTranslation} from 'react-i18next';
import i18n from 'i18n';
import api from '../../../api';
import {useReservation} from 'context/reservation';
import {PATHS} from 'Routes';
import {TestIds} from '../utils';
import {usePaymentSettings} from 'hooks/usePaymentSettings';
import {ReservationPayment} from '../../../utils/types';
import {useLocation} from 'react-router-dom';
import {useRegisterSteps, useCartDeals, useHousingExemptions} from '../../../hooks/';
import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
import {
  useErrorModal,
  useIsMounted,
  useModalControls,
  useStatus,
} from '../../../utils/hooks';
import {GUEST_APP_EVENTS_NAME, RESERVATION_PAYMENT_TYPES} from '../../../utils/constants';
import editIcon from 'assets/icons/edit-guest-icon.svg';
import deleteGuestIcon from 'assets/delete-guest-icon.png';
import StyledIcon from '../../acommon/StyledIcon';
import Link from 'components/acommon/Link';
import Section from './Section';
import Loader from '../../Loader';
import {DeleteButton} from '../../acommon/BookingStatus/styled';
import {
  SummaryWrapper,
  SummaryItem as SummaryItemStyled,
  SummaryTitle,
  SummaryPrice,
  SubmitButtons,
  TotalWrapper,
  TotalTitle,
  TotalPrice,
  SubmitButtonWrapper,
  LinkStyled,
  ButtonStyled,
  ModalDeleteDeal,
  ModalDeleteDealButton,
} from './styled';
import useMixPanel from 'hooks/useMixPanel';

export enum FORM_NAMES {
  experiencesCount = 'experiencesCount',
}

export type FormTypes = {
  [FORM_NAMES.experiencesCount]: number;
};

const PAYMENTS_TRANSLATION_TITLES: {
  [key in RESERVATION_PAYMENT_TYPES]?: string;
} = {
  [RESERVATION_PAYMENT_TYPES.booking]: 'booking',
  [RESERVATION_PAYMENT_TYPES.touristTaxes]: 'taxes',
};

export const validationSchema = yup.object({
  [FORM_NAMES.experiencesCount]: yup
    .number()
    .required(i18n.t('required'))
    .positive()
    .integer(),
});

type SummaryItemProps = {
  title: string | JSX.Element;
  price: number | string;
  currency: string;
  children?: React.ReactNode | JSX.Element | null;
};
function SummaryItem({title, price, currency, children}: SummaryItemProps) {
  return (
    <SummaryItemStyled data-testid={TestIds.summaryPaymentItem}>
      <SummaryTitle children={title} />
      <div>{children}</div>
      <SummaryPrice
        data-testid={TestIds.summaryPaymentItemPrice}
        children={`${price}${currency}`}
      />
    </SummaryItemStyled>
  );
}

type TotalProps = {
  title: string | JSX.Element;
  price: number | string;
  currency: string;
  isLoading?: boolean;
  currencyLabel?: string;
};
export function Total({
  title,
  price,
  currency,
  isLoading,
  currencyLabel = '',
}: TotalProps) {
  return (
    <TotalWrapper>
      <TotalTitle
        children={
          <>
            {title} <span>{`(${currencyLabel?.toUpperCase()})`}</span>
          </>
        }
      />
      {isLoading ? (
        <Loader color="gray" />
      ) : (
        <TotalPrice
          data-testid={TestIds.summaryTotalPrice}
          children={`${price}${currency}`}
        />
      )}
    </TotalWrapper>
  );
}

const Taxes = (props: SummaryItemProps) => {
  const location = useLocation();

  return (
    <SummaryItem {...props}>
      <Link
        data-testid={TestIds.openTaxesModalLink}
        to={{pathname: PATHS.payTouristTaxes, state: location.state}}
      >
        <img src={editIcon} alt="edit" />
      </Link>
    </SummaryItem>
  );
};

type UpsellingItemProps = SummaryItemProps & {
  setDealDeletionIdAndOpenConfirmationModal: (id: string) => void;
  disabled: boolean;
  id: string;
};
const Upselling = (props: UpsellingItemProps) => {
  const {setDealDeletionIdAndOpenConfirmationModal, disabled, id} = props;
  return (
    <SummaryItem {...props}>
      <DeleteButton
        disabled={disabled}
        onClick={() => !disabled && setDealDeletionIdAndOpenConfirmationModal(id)}
      >
        <StyledIcon name="removeguest" color="#6B6B95" />
      </DeleteButton>
    </SummaryItem>
  );
};

type SummaryFieldsMemoProps = {
  payments: ReservationPayment[];
  totalPrice: number | string;
};
const SummaryFieldsMemo = React.memo(function SummaryFields({
  payments,
  totalPrice,
}: SummaryFieldsMemoProps) {
  const {t, i18n} = useTranslation();
  const {ErrorModal, displayError} = useErrorModal();
  const isMounted = useIsMounted();
  const {isLoading, setStatus} = useStatus();
  const {refreshReservation} = useReservation();
  const {paymentSettingsCurrencySign: currency, paymentSettingsCurrency: currencyLabel} =
    usePaymentSettings();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    openModal: openDeleteConfirmationModal,
    closeModal: closeDeleteConfirmationModal,
  } = useModalControls();
  const {deleteCartDealFromStorage} = useCartDeals({onError: displayError});
  const [deletionFieldId, setDeletionFieldId] = React.useState('');

  const fetchDealId = async () => {
    const {error, data} = await api.upselling.getDeals(
      `reservation_payment=${deletionFieldId}`,
    );

    if (!isMounted.current) {
      return;
    }

    setDeletionFieldId('');

    if (error) {
      setStatus('idle');
      displayError(error);
      return;
    }

    const dealId = data?.[0]?.id as string;
    const offerId = data?.[0]?.offer?.id as string;

    if (!dealId) {
      setStatus('idle');
      displayError('Missing deal id.');
      return;
    }

    return {dealId, offerId};
  };

  const deleteDeal = async (id: string, offerId: string) => {
    const {error} = await api.upselling.deleteDeal(id);
    deleteCartDealFromStorage(offerId);

    if (!isMounted.current) {
      return;
    }

    if (error) {
      setStatus('idle');
      setDeletionFieldId('');
      displayError(error);
      return;
    }

    await refreshReservation();
    setStatus('idle');

    if (isDeleteConfirmationModalOpen) {
      closeDeleteConfirmationModal();
    }
  };

  const fetchDealIdAndDeleteDeal = async () => {
    setStatus('loading');
    const {dealId, offerId} = (await fetchDealId()) || {};
    if (!dealId || !offerId) {
      return;
    }

    deleteDeal(dealId, offerId);
  };

  const setDealDeletionIdAndOpenConfirmationModal = React.useCallback(
    (id: string) => {
      setDeletionFieldId(id);
      openDeleteConfirmationModal();
    },
    [openDeleteConfirmationModal],
  );

  const handleDeleteConfirmationModalClose = () => {
    setDeletionFieldId('');
    closeDeleteConfirmationModal();
  };

  const reservationPayments = React.useMemo(
    function ReservationPaymentsMemo() {
      return (payments || []).map((payment: ReservationPayment) => {
        if (payment.type === RESERVATION_PAYMENT_TYPES.upselling) {
          return (
            <Upselling
              currency={currency}
              key={payment.id}
              title={payment.name}
              price={payment.amount}
              id={payment.id}
              disabled={deletionFieldId === payment.id || isLoading}
              setDealDeletionIdAndOpenConfirmationModal={
                setDealDeletionIdAndOpenConfirmationModal
              }
            />
          );
        } else if (payment.type === RESERVATION_PAYMENT_TYPES.touristTaxes) {
          const title =
            t(PAYMENTS_TRANSLATION_TITLES.TOURIST_TAXES || '') || payment.name;

          return (
            <Taxes
              currency={currency}
              key={payment.id}
              title={title}
              price={payment.amount}
            />
          );
        } else {
          const title = i18n.exists(PAYMENTS_TRANSLATION_TITLES[payment.type] || '')
            ? t(PAYMENTS_TRANSLATION_TITLES[payment.type] || '')
            : payment.name;

          return (
            <SummaryItem
              currency={currency}
              key={payment.id}
              title={title}
              price={payment.amount}
            />
          );
        }
      });
    },
    [
      currency,
      deletionFieldId,
      i18n,
      isLoading,
      payments,
      setDealDeletionIdAndOpenConfirmationModal,
      t,
    ],
  );

  return (
    <>
      {reservationPayments}
      <Total
        currency={currency}
        currencyLabel={currencyLabel}
        title={t('total')}
        price={totalPrice}
      />
      <ModalDeleteDeal
        withCloseButton
        closeOnEscape
        closeOnDocumentClick
        iconSrc={deleteGuestIcon}
        iconProps={{width: 110, height: 110}}
        title={t('delete_payment_question')}
        text={t('you_are_going_to_delete_payment')}
        open={isDeleteConfirmationModalOpen}
        onClose={handleDeleteConfirmationModalClose}
      >
        <ModalDeleteDealButton
          onClick={() => {
            closeDeleteConfirmationModal();
            fetchDealIdAndDeleteDeal();
          }}
          label={t('delete')}
        />
      </ModalDeleteDeal>
      <ErrorModal />
    </>
  );
});

type SummaryProps = {
  onSubmit?: (a: any) => void;
  disabled?: boolean;
  className?: string;
};
function Summary({onSubmit, disabled, className}: SummaryProps) {
  const {t} = useTranslation();
  const location = useLocation();
  const {paymentsDetails, reservation} = useComputedReservationDetails();
  const {count, getCurrentStep} = useRegisterSteps();
  const payments = reservation?.reservation_payments as ReservationPayment[];
  const totalPrice = reservation?.total_amount_to_pay || 0;
  const isDisabledButton = Boolean(disabled || !parseFloat(totalPrice));

  const areBookingPaymentsNotPaidAndRequired =
    !paymentsDetails?.booking.paid && paymentsDetails?.booking.required;
  const areTaxesActivatedAndNotPaid =
    paymentsDetails?.taxes.activated && !paymentsDetails?.taxes.paid;
  const areBookingPaymentsPaidOrNotActivated =
    !paymentsDetails?.booking.activated || paymentsDetails?.booking.paid;
  const canPayLater = !(
    areBookingPaymentsNotPaidAndRequired ||
    (areTaxesActivatedAndNotPaid && areBookingPaymentsPaidOrNotActivated)
  );

  const {exemptions} = useHousingExemptions(
    reservation?.source_name,
    reservation?.housing?.id,
  );

  const {PaymentEvents} = useMixPanel();

  const filteredPayments = React.useMemo(
    function filterPayments() {
      if (!exemptions || !payments) return [];

      const {taxExemption, bookingPaymentExemption} = exemptions;
      const resultPayments = [];

      if (!taxExemption) {
        const taxes = payments.find(
          field =>
            field.type === RESERVATION_PAYMENT_TYPES.touristTaxes &&
            field.status === 'ENABLED' &&
            parseFloat(field.amount) > 0,
        );

        if (taxes) {
          resultPayments.push(taxes);
        }
      }

      if (!bookingPaymentExemption && paymentsDetails?.booking.activated) {
        const booking = payments.find(
          field =>
            (field.type === RESERVATION_PAYMENT_TYPES.booking ||
              field.type === RESERVATION_PAYMENT_TYPES.pmsIntegration) &&
            field.status === 'ENABLED' &&
            parseInt(field.amount, 10) > 0,
        );

        if (booking) {
          resultPayments.push(booking);
        }

        const extraServices = payments?.filter(field => {
          return (
            field.type === RESERVATION_PAYMENT_TYPES.extraService &&
            field.status === 'ENABLED' &&
            parseInt(field.amount, 10) > 0
          );
        });

        if (extraServices?.length) {
          resultPayments.push(...extraServices);
        }
      }

      const upselling = payments.filter(field => {
        return (
          field.type === RESERVATION_PAYMENT_TYPES.upselling &&
          field.status === 'ENABLED' &&
          parseInt(field.amount, 10) > 0
        );
      });

      if (upselling && upselling.length >= 0) {
        resultPayments.push(...upselling);
      }

      return resultPayments;
    },
    [paymentsDetails?.booking.activated, exemptions, payments],
  );

  React.useEffect(() => {
    if (!payments) return;
    payments.forEach(p => {
      if (p.status === 'ENABLED') {
        PaymentEvents(GUEST_APP_EVENTS_NAME.paymentRequest, {
          type: p.type,
          paymentId: p.id,
        });
      }
    });
  }, [payments, PaymentEvents]);

  return (
    <SummaryWrapper className={className}>
      <Section title={t('price_details')}>
        <SummaryFieldsMemo totalPrice={totalPrice} payments={filteredPayments} />
        <SubmitButtons>
          <SubmitButtonWrapper>
            <div className="progress">{`${getCurrentStep(PATHS.pay)}/${count}`}</div>
            <ButtonStyled
              disabled={isDisabledButton}
              onClick={onSubmit}
              type="submit"
              iconRight="arrowNext"
              label={t('confirm_and_pay')}
            />
          </SubmitButtonWrapper>
          {canPayLater && (
            <LinkStyled
              to={{pathname: PATHS.finish, state: location.state}}
              children={t('pay_later')}
            />
          )}
        </SubmitButtons>
      </Section>
    </SummaryWrapper>
  );
}

export default Summary;
